import {
	QUERY_KEYS,
	SUBSCRIPTION_STATUS,
	subscriptionRules,
} from "@/libraries/constant";
import { isAuthenticating } from "@/libraries/helper";
import type {
	GetMyAlertsParamsSchema,
	ParamsGetMembersSchema,
	ParamsGetMpisMembersSchema,
} from "@/schemas";
import { memberService } from "@/services";
import { mpisService } from "@/services/mpis";
import {
	type UnusedSkipTokenOptions,
	infiniteQueryOptions,
	queryOptions,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import dayjs from "dayjs";
import isEmpty from "lodash/isEmpty";
import orderBy from "lodash/orderBy";

export const memberProfileQueryOptions = () => {
	return queryOptions({
		queryKey: [QUERY_KEYS.GET_MEMBER_PROFILE],
		queryFn: () => memberService.getMemberProfile(),
		enabled: isAuthenticating(),
		select: (res) => {
			const isFreePlan =
				!res.user?.subscription_status ||
				res.user?.subscription_status?.includes("free");
			const isProPlan =
				res.user?.subscription_status &&
				[SUBSCRIPTION_STATUS.PRO, SUBSCRIPTION_STATUS.PREMIUM].includes(
					res.user?.subscription_status.toLowerCase() as any,
				) &&
				res.user?.subscription_plan?.status === "active";
			const isPlusPlan =
				res.user?.subscription_status &&
				[SUBSCRIPTION_STATUS.PLUS].includes(
					res.user?.subscription_status.toLowerCase() as any,
				) &&
				res.user?.subscription_plan?.status === "active";

			const maxCase = isFreePlan
				? subscriptionRules.maxCase[SUBSCRIPTION_STATUS.Free]
				: isPlusPlan
					? subscriptionRules.maxCase[SUBSCRIPTION_STATUS.PLUS]
					: Number.POSITIVE_INFINITY;
			const subsribeBulletin = isFreePlan
				? subscriptionRules.subsBulletin[SUBSCRIPTION_STATUS.Free]
				: Number.POSITIVE_INFINITY;

			return {
				...res,
				user: {
					...res.user,
					_isFreePlan: isFreePlan,
					_isProPlan: isProPlan,
					_isPlusPlan: isPlusPlan,
					_maxCase: maxCase,
					_maxSubsribeBulletin: subsribeBulletin,
				},
			};
		},
	});
};

export const getMembersQueryOptions = (
	params: ParamsGetMembersSchema,
	options?: { enabled: boolean },
) => {
	const queryKey = isEmpty(params)
		? [QUERY_KEYS.GET_MEMBERS]
		: [QUERY_KEYS.GET_MEMBERS, params];
	return infiniteQueryOptions({
		queryKey,
		initialPageParam: 1,
		queryFn: ({ pageParam, signal }) =>
			memberService.getMembers({ ...params, page: pageParam || 1 }, { signal }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (data) => data.pages?.flatMap((page) => page.users) ?? [],
		...options,
	});
};

export const getMemberQueryOptions = (
	id: any,
	options?: { enabled: boolean },
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_MEMBER, id],
		queryFn: () => memberService.getMember(+id),
		enabled: options?.enabled ?? !!id,
		...options,
	});

export const getMyAlertsQueryOptions = (params?: GetMyAlertsParamsSchema) =>
	infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_MY_ALERTS, ...(isEmpty(params) ? [] : [params])],
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			memberService.alerts({ ...params, page: pageParam, limit: 25 }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (res) => res?.pages?.flatMap((p) => p.messages) || [],
	});

export const getMyAlertsMetaQueryOptions = (params?: GetMyAlertsParamsSchema) =>
	queryOptions({
		queryKey: [
			QUERY_KEYS.GET_MY_ALERTS_FLAT,
			...(isEmpty(params) ? [] : [params]),
		],
		queryFn: () =>
			memberService.alerts({
				...params,
				page: params?.page ?? 1,
				limit: params?.limit ?? 25,
			}),
	});

export const getMemberChatsOptions = () =>
	infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_MEMBER_CHATS],
		initialPageParam: 1,
		queryFn: ({ pageParam }) =>
			memberService.chats({ page: pageParam, limit: 25 }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (res) => {
			const chats = res?.pages?.flatMap((p) => p.users) || [];
			if (!chats.length) return [];
			const orderedChats = orderBy(
				chats.map((m) => ({
					...m,
					date: !m?.last_message?.updated_at
						? dayjs(m.updated_at)
						: m.last_message.updated_at,
				})),
				(chat) => chat.date,
				"desc",
			);
			return orderedChats;
		},
	});

export const getMemberChatsMetaOptions = ({ page = 1, limit = 25 }) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_MEMBER_CHATS_FLAT],
		queryFn: () => memberService.chats({ page, limit }),
	});

export const getMpisMembersQueryOptions = (
	params: ParamsGetMpisMembersSchema = {},
	options?: { enabled: boolean },
) => {
	return infiniteQueryOptions({
		queryKey: [QUERY_KEYS.GET_MPIS_MEMBERS],
		initialPageParam: 1,
		queryFn: ({ pageParam, signal }) =>
			mpisService.getMembers({ ...params, page: pageParam || 1 }, { signal }),
		getNextPageParam: (lastPage) => lastPage?.meta?.next_page,
		select: (data) => data.pages?.flatMap((page) => page.users) ?? [],
		...options,
	});
};

export const getMemberHomeQueryOptions = (
	options: Omit<
		UnusedSkipTokenOptions<
			Awaited<ReturnType<typeof memberService.home>>,
			AxiosError,
			Awaited<ReturnType<typeof memberService.home>>,
			"GET_MEMBER_HOME"[]
		>,
		"queryKey" | "queryFn"
	> = {},
) =>
	queryOptions({
		queryKey: [QUERY_KEYS.GET_MEMBER_HOME],
		queryFn: ({ signal }) => memberService.home({ signal }),
		...options,
	});
